import { useEffect, useRef } from 'react';

import { useFeatureFlagSplit } from '@verantos-dev/app-state-base';
import { useCohortDefinitionStore } from '@verantos-dev/app-state-cohort-definition';
import { avKeys } from '@verantos-dev/feature-analytical-variables';
import { useResearchAssistantSocketContext } from '@verantos-dev/feature-research-assistant';
import type { CohortDefinitionStatus } from '@verantos-dev/types-cohorts';

import { useStoreHydration } from './useStoreHydration';
import { useVersionNavigation } from './useVersionNavigation';

/**
 * Reset transient store flags on any authoritative socket event.
 *
 * `clearStagedChanges` controls whether `hasStagedChanges` is reset:
 * - `true` for cohort_saved / cohort_reverted (explicit user action, data is authoritative)
 * - `false` for staged_changes_updated (auto-save; let setFromServer clear it
 *   after fresh TQ data arrives, preventing stale cache from overwriting optimistic updates)
 */
const resetStoreOnSocketEvent = (newStatus?: string, clearStagedChanges = true) => {
  useCohortDefinitionStore.getState().resetOnSocketEvent({
    newStatus: newStatus as CohortDefinitionStatus | undefined,
    clearStagedChanges,
  });
};

/** Data passed to the onStagedChangesUpdated callback. */
export interface StagedChangesUpdatedEventData {
  versionId?: string;
  newStatus?: string;
}

/** Data passed to the onCohortSaved callback. */
export interface CohortSavedEventData {
  newStatus?: string;
}

/** Data passed to the onCohortReverted callback. */
export interface CohortRevertedEventData {
  cohortAction?: string;
  systemMessage?: { text: string; timestamp: string };
  newStatus?: string;
  versionId?: string;
}

/**
 * Optional callbacks fired on socket events.
 * Used by the caller to dispatch Redux actions (RA messages, isSaving, RTK cache patches, etc.)
 * without coupling this lib to Redux.
 */
export interface SocketEventCallbacks {
  onStagedChangesUpdated?: (data: StagedChangesUpdatedEventData) => void;
  onCohortSaved?: (data: CohortSavedEventData) => void;
  onCohortReverted?: (data: CohortRevertedEventData) => void;
}

/**
 * Listens to cohort socket events and routes them to the appropriate actions:
 * - TQ cache invalidation
 * - Zustand store hydration / reset
 * - Silent URL navigation on version changes
 * - Caller-provided Redux sync callbacks (RA messages, isSaving, RTK cache patches, etc.)
 */
export const useCohortDefinitionSocket = (
  cohortVersionId: string,
  params: { projectId: string; cohortId: string },
  enabled = false,
  callbacks?: SocketEventCallbacks
) => {
  const { socket } = useResearchAssistantSocketContext();
  const { handleVersionNavigation, cohortVersionIdRef } = useVersionNavigation({
    ...params,
    cohortVersionId,
  });
  const { hydrateVersion, queryClient } = useStoreHydration(params);

  // TODO: cleanup with explicit_save ff
  const isExplicitSave = useFeatureFlagSplit('explicit_save');
  const isExplicitSaveRef = useRef(isExplicitSave);
  isExplicitSaveRef.current = isExplicitSave;

  // Ref to avoid re-registering socket listeners when callbacks change
  const callbacksRef = useRef(callbacks);
  callbacksRef.current = callbacks;

  useEffect(() => {
    if (!socket || !enabled) return;

    const handleStagedChangesUpdated = async (data: {
      hasStagedChanges?: boolean;
      versionId?: string;
      newStatus?: string;
    }) => {
      // TODO: cleanup with explicit_save ff
      // On explicit_save: this event is the sole source of truth for hasStagedChanges
      // (set on subscribe and after every BE-confirmed write). isSaving clears here as
      // the round-trip end. DSL flows via cohort_dsl/REST, status via
      // cohort_saved/cohort_reverted, version changes via cohort_dsl.
      if (isExplicitSaveRef.current) {
        useCohortDefinitionStore.setState({ hasStagedChanges: !!data.hasStagedChanges });
        useCohortDefinitionStore.getState().setIsSaving(false);
        return;
      }

      try {
        const eventVersionId = data.versionId ?? cohortVersionIdRef.current;
        const currentVersionId = cohortVersionIdRef.current;
        const versionChanged = eventVersionId !== currentVersionId;

        // Clear hasStagedChanges when the BE confirms no real changes (net-zero edit).
        // Without this, store mutations (e.g. deleteAVCriterion) set hasStagedChanges=true
        // and it stays true until hydrateVersion completes, causing a button flicker.
        const clearStagedChanges = data.hasStagedChanges === false;
        resetStoreOnSocketEvent(data.newStatus, clearStagedChanges);
        if (versionChanged) {
          useCohortDefinitionStore.setState((state) => ({
            profile: { ...state.profile, latestCohortVersionId: eventVersionId },
          }));
        }

        // Notify caller (Redux sync: setIsSaving, clearLocalUnsavedChanges, RTK cache patches)
        callbacksRef.current?.onStagedChangesUpdated?.({
          versionId: versionChanged ? eventVersionId : undefined,
          newStatus: data.newStatus,
        });

        // If version changed, navigate to the new version
        if (versionChanged) {
          handleVersionNavigation(eventVersionId);
        }

        // Invalidate all profile cache entries (not just the new version's).
        // When a version changes (e.g. V1→V2 via RA), the old V1 profile cache still
        // holds latestCohortVersionId:V1. If the user navigates back via a V1 URL,
        // that stale entry overwrites the store and renders old content.
        await queryClient.invalidateQueries({ queryKey: avKeys.profiles() });

        // Skip definition hydration when the server confirms there are still staged
        // changes and no version change. In this case the store already holds the
        // correct state — either from the user's optimistic edits or from RA applying
        // a DSL update via setFromServer(). Hydrating here would pull a potentially
        // stale TQ cache entry (fetched before RA applied its changes) and overwrite
        // the correct store state, causing RA-added criteria to briefly disappear.
        //
        // When versionChanged we must hydrate to load the new version's definition.
        // When data.hasStagedChanges is explicitly false the server confirms the edit
        // was net-zero, so hydration is safe and needed to reconcile the store.
        if (data.hasStagedChanges !== false && !versionChanged) return;

        // skipIfStagedChanges protects local optimistic state on version changes:
        // if the user has local edits when the fetch resolves, the store write is skipped.
        await hydrateVersion(eventVersionId, { skipIfStagedChanges: true });
      } catch (error) {
        console.error('[useCohortDefinitionSocket] staged_changes_updated failed:', error);
      }
    };

    const handleCohortSaved = async (data: { newStatus?: string }) => {
      // TODO: cleanup with explicit_save ff
      // On explicit_save, the post-save effect in useSaveCohortDefinitionModal owns these
      // side-effects. The backend no longer emits cohort_saved on FF on, so this guard
      // is belt-and-suspenders in case of a race or misconfig.
      if (isExplicitSaveRef.current) return;

      resetStoreOnSocketEvent(data.newStatus);

      // Notify caller (Redux sync: clearStagedFromResearchAssistant, clearLocalUnsavedChanges, RTK cache patches)
      callbacksRef.current?.onCohortSaved?.({ newStatus: data?.newStatus });

      try {
        // avKeys.all covers profiles + definition — invalidate everything after a save.
        await queryClient.invalidateQueries({ queryKey: avKeys.all });
      } catch (error) {
        console.error('[useCohortDefinitionSocket] cohort_saved invalidation failed:', error);
      } finally {
        useCohortDefinitionStore.getState().clearChangeStatuses();
      }
    };

    const handleCohortReverted = async (data: {
      versionId?: string;
      cohortAction?: string;
      systemMessage?: { text: string; timestamp: string };
      newStatus?: string;
    }) => {
      try {
        const versionId = data.versionId ?? cohortVersionIdRef.current;
        const currentVersionId = cohortVersionIdRef.current;

        // Navigate to the reverted version (may be a different version)
        if (versionId !== currentVersionId) {
          handleVersionNavigation(versionId);
        }

        resetStoreOnSocketEvent(data.newStatus);
        if (versionId !== currentVersionId) {
          useCohortDefinitionStore.setState((state) => ({
            profile: { ...state.profile, latestCohortVersionId: versionId },
          }));
        }

        await queryClient.invalidateQueries({ queryKey: avKeys.profiles() });
        // The publish operation updates the committed version's content without changing its ID.
        // Invalidate the list cache so hydrateVersion fetches fresh data rather than the
        // pre-publish stale entry that was populated on initial page load.
        await queryClient.invalidateQueries({ queryKey: avKeys.list(versionId) });
        await hydrateVersion(versionId);

        // Notify caller (Redux sync: RA messages, clearStagedFromResearchAssistant, clearLocalUnsavedChanges, RTK cache patches)
        callbacksRef.current?.onCohortReverted?.({
          cohortAction: data.cohortAction,
          systemMessage: data.systemMessage,
          newStatus: data.newStatus,
          versionId: data.versionId,
        });
      } catch (error) {
        console.error('[useCohortDefinitionSocket] cohort_reverted failed:', error);
      }
    };

    socket.on('staged_changes_updated', handleStagedChangesUpdated);
    socket.on('cohort_saved', handleCohortSaved);
    socket.on('cohort_reverted', handleCohortReverted);

    return () => {
      socket.off('staged_changes_updated', handleStagedChangesUpdated);
      socket.off('cohort_saved', handleCohortSaved);
      socket.off('cohort_reverted', handleCohortReverted);
    };
  }, [socket, queryClient, enabled, handleVersionNavigation, cohortVersionIdRef, hydrateVersion]);
};
